'use client';
import React, { useState } from 'react';
import { Shield, Lock, Check, Loader2, AlertTriangle, X, Info } from 'lucide-react';
import { toast } from 'sonner';

interface JoinSidebarProps {
  joinModalOpen: boolean;
  onOpenJoin: () => void;
  onCloseJoin: () => void;
}

export default function JoinSidebar({ joinModalOpen, onOpenJoin, onCloseJoin }: JoinSidebarProps) {
  const [step, setStep] = useState<'confirm' | 'loading' | 'success'>('confirm');
  const [agreed, setAgreed] = useState(false);

  const handleConfirm = async () => {
    if (!agreed) {
      toast.error('Please acknowledge the entry terms to proceed');
      return;
    }
    setStep('loading');
    // Backend integration point: POST /api/competitions/:id/join with escrow lock
    await new Promise((r) => setTimeout(r, 1800));
    setStep('success');
    toast.success('Successfully joined! $10 locked in escrow. Good luck! 🎉');
  };

  return (
    <>
      {/* Sticky sidebar card */}
      <div className="sticky top-4 space-y-4">
        <div className="rounded-xl p-5" style={{ background: 'var(--card)', border: '1px solid rgba(124,58,237,0.3)' }}>
          <div className="text-center mb-5">
            <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#A78BFA' }}>Entry Fee</p>
            <p className="text-4xl font-extrabold text-foreground mb-1" style={{ fontVariantNumeric: 'tabular-nums' }}>$10.00</p>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>USD · Held in escrow until result</p>
          </div>

          {/* Fee breakdown */}
          <div className="space-y-2 mb-5 p-3 rounded-xl" style={{ background: 'var(--muted)' }}>
            <div className="flex justify-between text-xs">
              <span style={{ color: 'var(--muted-foreground)' }}>Entry amount</span>
              <span className="font-semibold text-foreground">$10.00</span>
            </div>
            <div className="flex justify-between text-xs">
              <span style={{ color: 'var(--muted-foreground)' }}>Platform service fee</span>
              <span className="font-semibold" style={{ color: '#94A3B8' }}>10% (from prize pool)</span>
            </div>
            <div className="flex justify-between text-xs">
              <span style={{ color: 'var(--muted-foreground)' }}>Your wallet balance</span>
              <span className="font-semibold" style={{ color: '#10B981' }}>$124.50 ✓</span>
            </div>
            <div className="h-px" style={{ background: 'var(--border)' }} />
            <div className="flex justify-between text-sm">
              <span className="font-semibold text-foreground">You pay today</span>
              <span className="font-extrabold" style={{ color: '#A78BFA', fontVariantNumeric: 'tabular-nums' }}>$10.00</span>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-2 mb-5">
            {[
              { label: 'Max Prize', value: '$2,500', color: '#F59E0B' },
              { label: 'Win Chance', value: '~0.04%', color: '#94A3B8' },
              { label: 'Participants', value: '2,847', color: '#06B6D4' },
              { label: 'Seats Left', value: '2,153', color: '#10B981' },
            ].map((s) => (
              <div key={`js-${s.label}`} className="text-center p-2 rounded-lg" style={{ background: 'var(--muted)' }}>
                <p className="text-sm font-bold" style={{ color: s.color, fontVariantNumeric: 'tabular-nums' }}>{s.value}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)', fontSize: '10px' }}>{s.label}</p>
              </div>
            ))}
          </div>

          <button
            onClick={onOpenJoin}
            className="btn-primary w-full justify-center py-3 text-base glow-violet"
          >
            <Lock size={16} />
            Confirm Entry — $10
          </button>

          <div className="flex items-center justify-center gap-2 mt-3 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <Shield size={12} />
            <span>Funds secured by Brain Basket Escrow</span>
          </div>
        </div>

        {/* Fair play card */}
        <div className="rounded-xl p-4" style={{ background: 'rgba(6,182,212,0.05)', border: '1px solid rgba(6,182,212,0.2)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Shield size={14} style={{ color: '#06B6D4' }} />
            <span className="text-xs font-bold" style={{ color: '#06B6D4' }}>Fair Play Guaranteed</span>
          </div>
          <ul className="space-y-1.5">
            {[
              'Server-authoritative scoring',
              'Anti-cheat & bot detection',
              'Result dispute available 24h',
              'Transparent odds & rules',
            ].map((item) => (
              <li key={`fp-${item}`} className="flex items-center gap-2 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                <Check size={10} style={{ color: '#10B981', flexShrink: 0 }} />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Join modal */}
      {joinModalOpen && (
        <div className="modal-overlay" onClick={onCloseJoin}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'var(--border)' }}>
              <h3 className="text-base font-bold text-foreground">
                {step === 'success' ? 'You\'re In!' : 'Confirm Competition Entry'}
              </h3>
              {step !== 'loading' && (
                <button onClick={onCloseJoin} className="p-1 rounded-lg hover:bg-muted transition-colors" aria-label="Close modal">
                  <X size={16} style={{ color: 'var(--muted-foreground)' }} />
                </button>
              )}
            </div>

            <div className="p-5">
              {step === 'confirm' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-xl" style={{ background: 'var(--muted)' }}>
                    <p className="text-sm font-bold text-foreground mb-3">National Trivia Championship — Q3 2026</p>
                    <div className="space-y-2">
                      {[
                        { label: 'Entry Fee', value: '$10.00 USD', highlight: true },
                        { label: 'Prize Pool', value: '$5,000 (your share: up to $2,500)' },
                        { label: 'Platform Fee', value: '10% deducted from prize pool' },
                        { label: 'Refund Policy', value: 'Non-refundable once competition starts' },
                        { label: 'Funds Status', value: 'Locked in escrow until result' },
                      ].map((item) => (
                        <div key={`modal-${item.label}`} className="flex justify-between text-xs">
                          <span style={{ color: 'var(--muted-foreground)' }}>{item.label}</span>
                          <span className="font-semibold" style={{ color: item.highlight ? '#A78BFA' : 'var(--foreground)', fontVariantNumeric: 'tabular-nums' }}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-xl"
                    style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)' }}>
                    <AlertTriangle size={14} style={{ color: '#F59E0B', flexShrink: 0, marginTop: '1px' }} />
                    <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                      By confirming, $10.00 will be deducted from your wallet and held in secure escrow. Winning is not guaranteed. Please only enter competitions within your budget.
                    </p>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-primary flex-shrink-0"
                    />
                    <span className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                      I confirm I have read the rules, understand the entry fee is non-refundable once the competition begins, and I am entering voluntarily within my personal budget.
                    </span>
                  </label>

                  <div className="flex gap-3">
                    <button onClick={onCloseJoin} className="btn-ghost flex-1 justify-center py-2.5">
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirm}
                      className="btn-primary flex-1 justify-center py-2.5"
                      style={{ opacity: agreed ? 1 : 0.5 }}
                    >
                      <Lock size={14} />
                      Confirm Entry — $10
                    </button>
                  </div>
                </div>
              )}

              {step === 'loading' && (
                <div className="flex flex-col items-center justify-center py-10 gap-4">
                  <Loader2 size={32} className="animate-spin" style={{ color: '#A78BFA' }} />
                  <div className="text-center">
                    <p className="text-sm font-semibold text-foreground">Processing your entry...</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>Securing funds in escrow</p>
                  </div>
                </div>
              )}

              {step === 'success' && (
                <div className="flex flex-col items-center justify-center py-8 gap-4 text-center">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(16,185,129,0.2)', border: '2px solid #10B981' }}>
                    <Check size={28} style={{ color: '#10B981' }} />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-foreground mb-1">Successfully Joined!</p>
                    <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>$10.00 locked in escrow. Competition starts in 4h 22m.</p>
                  </div>
                  <div className="w-full p-3 rounded-xl" style={{ background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.25)' }}>
                    <div className="flex items-center gap-2 text-sm">
                      <Info size={14} style={{ color: '#A78BFA' }} />
                      <span style={{ color: '#A78BFA' }}>You&apos;ll receive a notification when the competition starts</span>
                    </div>
                  </div>
                  <button onClick={() => { onCloseJoin(); setStep('confirm'); setAgreed(false); }}
                    className="btn-primary w-full justify-center py-2.5">
                    View My Competitions
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}